import { PathRoute, useNav, useRouter } from "@pomle/react-router-paths";
import { Sticky, ViewStack } from "@pomle/react-viewstack";
import { ReactComponent as QuestionIcon } from "assets/icons/chat/chat-28x28.svg";
import { ReactComponent as ExIcon } from "assets/icons/ex/ex-28x28.svg";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePopup } from "render/context/PopupContext";
import { useAppointmentSummariesQuery } from "render/hooks/api/queries/useAppointmentSummariesQuery";
import { useLatestScanQuery } from "render/hooks/api/queries/useLatestScanQuery";
import { useContactUsPopup } from "render/hooks/popups/useContactUsPopup";
import { useTracking } from "render/hooks/useTracking";
import { paths } from "render/routes/paths";
import { ErrorBoundary } from "render/ui/format/ErrorBoundary";
import { Backdrop } from "render/ui/layout/Backdrop";
import { FullScreenPageLayout } from "render/ui/layout/FullScreenPageLayout";
import { LogoHeader } from "render/ui/layout/LogoHeader";
import { Nav } from "render/ui/layout/Nav";
import { IconButton } from "render/ui/trigger/IconButton";
import { MetricABIDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricABI/Details";
import { MetricAnklePressureDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricAnklePressure/Details";
import { MetricBMIDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricBMI/Details";
import { MetricBasophilsDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricBasophils/Details";
import { MetricBloodOxygenDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricBloodOxygen/Details";
import { MetricBloodPressureDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricBloodPressure";
import { MetricHDLDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricBloodworkHdl/Details";
import { MetricLDLDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricBloodworkLdl/Details";
import { MetricNonHDLDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricBloodworkNonHdl/Details";
import { MetricHsCRPDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricCRP/Details";
import { MetricCholesterolDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricCholesterol/Details";
import { MetricEosinophilsDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricEosinophils/Details";
import { MetricEyePressureDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricEyePressure/Details";
import { MetricGlucoseDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricGlucose/Details";
import { MetricGripStrengthDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricGripStrength/Details";
import { MetricHbA1cDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricHBA1C/Details";
import { MetricHaemoglobinDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricHaemoglobin/Details";
import { MetricHeartRateDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricHeartRate/Details";
import { MetricLymphocytesDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricLymphocytes/Details";
import { MetricNeutrophilsDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricNeutrophils/Details";
import { MetricSkinDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricSkinLesions";
import { MetricTriglyceridesDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricTriglycerides/Details";
import { MetricWeightDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricWeight/Details";
import { MetricWhiteBloodCellsDetails } from "render/views/Report/AppointmentDataView/components/MetricsSection/components/metrics/MetricWhiteBloodCells/Details";
import { AvatarSection } from "./components/AvatarSection";
import { ScanDataTab } from "./components/ScanDataTab";
import { SummariesTab } from "./components/SummariesTab/SummariesTab";
import styles from "./styles.module.sass";
import { reportEvent } from "./tracking";
import { Trans } from "./trans";

const MOBILE_BREAKPOINT = 980;
const MOBILE_OFFSET = 100;
const DESKTOP_OFFSET = 60 * 2;

const MetricToRoute = [
  {
    metric: "bloodPressure",
    route: paths.bloodPressureMetric,
    component: MetricBloodPressureDetails,
  },
  {
    metric: "ankle",
    route: paths.anklePressureMetric,
    component: MetricAnklePressureDetails,
  },
  {
    metric: "abi",
    route: paths.abiMetric,
    component: MetricABIDetails,
  },
  {
    metric: "bloodOxygen",
    route: paths.bloodOxygenMetric,
    component: MetricBloodOxygenDetails,
  },
  {
    metric: "heartRate",
    route: paths.heartRateMetric,
    component: MetricHeartRateDetails,
  },
  {
    metric: "cholesterol",
    route: paths.cholesterolMetric,
    component: MetricCholesterolDetails,
  },
  {
    metric: "ldl",
    route: paths.ldlMetric,
    component: MetricLDLDetails,
  },
  {
    metric: "hdl",
    route: paths.hdlMetric,
    component: MetricHDLDetails,
  },
  {
    metric: "NonHDL",
    route: paths.nonHDLMetric,
    component: MetricNonHDLDetails,
  },
  {
    metric: "triglycerides",
    route: paths.triglyceridesMetric,
    component: MetricTriglyceridesDetails,
  },
  {
    metric: "whiteBloodCell",
    route: paths.whiteBloodCellMetric,
    component: MetricWhiteBloodCellsDetails,
  },
  {
    metric: "neutrophil",
    route: paths.neutrophilMetric,
    component: MetricNeutrophilsDetails,
  },
  {
    metric: "basophil",
    route: paths.basophilMetric,
    component: MetricBasophilsDetails,
  },
  {
    metric: "eosinophil",
    route: paths.eosinophilMetric,
    component: MetricEosinophilsDetails,
  },
  {
    metric: "lymphocyte",
    route: paths.lymphocyteMetric,
    component: MetricLymphocytesDetails,
  },
  {
    metric: "hsCRP",
    route: paths.hsCRPMetric,
    component: MetricHsCRPDetails,
  },
  {
    metric: "hbA1c",
    route: paths.hbA1cMetric,
    component: MetricHbA1cDetails,
  },
  {
    metric: "haemoglobin",
    route: paths.haemoglobinMetric,
    component: MetricHaemoglobinDetails,
  },
  {
    metric: "glucose",
    route: paths.glucoseMetric,
    component: MetricGlucoseDetails,
  },
  {
    metric: "weight",
    route: paths.weightMetric,
    component: MetricWeightDetails,
  },
  {
    metric: "bmi",
    route: paths.bmiMetric,
    component: MetricBMIDetails,
  },
  {
    metric: "gripStrength",
    route: paths.gripStrengthMetric,
    component: MetricGripStrengthDetails,
  },
  {
    metric: "eyePressure",
    route: paths.eyePressureMetric,
    component: MetricEyePressureDetails,
  },
  {
    metric: "skin",
    route: paths.skinMetric,
    component: MetricSkinDetails,
  },
];

export function ReportView() {
  const router = useRouter();
  const { trackEvent } = useTracking();
  const [transitioning, setTransitioning] = useState<boolean>(false);
  const [headerHeight, setHeaderHeight] = useState<number | undefined>(
    undefined
  );
  const headerRef = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const nav = {
    home: useNav(paths.root),
    summary: useNav(paths.appointmentSummary),
    summaryData: useNav(paths.appointmentSummaryData),
  };
  const popUpDialogContext = usePopup();
  const contactUsPopup = useContactUsPopup();
  const latestSkinScanQuery = useLatestScanQuery("skin/");
  const appointmentSummariesQuery = useAppointmentSummariesQuery();

  const avatarMaxHeight = useMemo(() => {
    if (headerHeight == null) {
      return null;
    }
    const offSet =
      document.body.clientWidth > MOBILE_BREAKPOINT
        ? DESKTOP_OFFSET
        : MOBILE_OFFSET;

    return Math.min(document.body.clientHeight - headerHeight - offSet, 1000);
  }, [headerHeight]);
  useEffect(() => {
    if (!transitioning) {
      return;
    }

    const id = window.setTimeout(() => {
      setTransitioning(false);

      if (!container.current) {
        return;
      }
      if (!headerRef.current) {
        return;
      }

      if (document.body.clientWidth > MOBILE_BREAKPOINT) {
        container.current.scrollTo({ top: 0 });
        return;
      }

      const header = headerRef.current.clientHeight;
      const top = window.document.body.clientHeight - header - MOBILE_OFFSET;

      if (container.current.scrollTop < top) {
        return;
      }

      container.current.scrollTo({ top });
    }, 400);

    return () => window.clearTimeout(id);
  }, [transitioning]);

  useEffect(() => {
    if (!headerRef.current) {
      return;
    }
    setHeaderHeight(headerRef.current.clientHeight);
  }, []);

  const isHeaderActive = useMemo(() => {
    const routes = [
      paths.appointmentSummary,
      paths.appointmentSummaryData,
    ] as const;

    return routes.some((route) => {
      const match = route.match(router.location.pathname);
      const result = match === 0;
      return result;
    });
  }, [router.location]);

  const latestSkinScan = latestSkinScanQuery.data?.[0];

  return (
    <ViewStack>
      <FullScreenPageLayout ref={container} aria-hidden={!isHeaderActive}>
        <div className={styles.contentContainer}>
          <div
            ref={headerRef}
            data-active={isHeaderActive}
            className={styles.headerSticky}
            data-blured={popUpDialogContext.isActive}
          >
            <LogoHeader
              leftElement={
                <IconButton
                  onClick={nav.home.on({})}
                  icon={<ExIcon />}
                  ariaLabel={Trans.Icons.Close()}
                />
              }
              rightElement={
                <IconButton
                  icon={<QuestionIcon display="block" />}
                  onClick={() => contactUsPopup.emit()}
                  ariaLabel={Trans.Icons.ContactUs()}
                />
              }
            />
            <div className={styles.navTabsContainer}>
              <div className={styles.navTabs}>
                <Nav>
                  <Nav.Item
                    label={<Trans.Tabs.Summary />}
                    path={paths.appointmentSummary}
                    params={{}}
                    onClick={() => {
                      setTransitioning(true);
                      trackEvent(reportEvent.navigate("summary"));
                    }}
                  />
                  <Nav.Item
                    label={<Trans.Tabs.ScanData />}
                    path={paths.appointmentSummaryData}
                    params={{}}
                    exact={false}
                    onClick={() => {
                      setTransitioning(true);
                      trackEvent(reportEvent.navigate("biomarkers"));
                    }}
                  />
                </Nav>
              </div>
            </div>
          </div>
          <div className={styles.flex}>
            <div className={styles.content}>
              {avatarMaxHeight && (
                <>
                  <ErrorBoundary
                    fallback={() => <div style={{ height: avatarMaxHeight }} />}
                  >
                    <AvatarSection
                      height={avatarMaxHeight}
                      skinScan={latestSkinScan}
                    />
                  </ErrorBoundary>
                  <div
                    className={styles.tabContentContainer}
                    data-active={isHeaderActive}
                  >
                    {latestSkinScan && (
                      <>
                        <PathRoute path={paths.appointmentSummary}>
                          {(match) => (
                            <div
                              data-transitioning={transitioning}
                              data-active={!!match?.exact}
                              className={styles.tabContent}
                            >
                              <SummariesTab
                                onGoToScanData={() => {
                                  setTransitioning(true);
                                  trackEvent(
                                    reportEvent.navigate("biomarkers")
                                  );
                                  nav.summaryData.go({});
                                }}
                                latestScanDate={latestSkinScan.timestamp}
                                visitsSummaries={appointmentSummariesQuery.data}
                              />
                            </div>
                          )}
                        </PathRoute>
                        <PathRoute path={paths.appointmentSummaryData}>
                          {(match) => (
                            <div
                              data-transitioning={transitioning}
                              data-active={!!match}
                              className={styles.tabContent}
                            >
                              <ScanDataTab
                                latestScanDate={latestSkinScan.timestamp}
                                headerHeight={headerHeight || 0}
                              />
                            </div>
                          )}
                        </PathRoute>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </FullScreenPageLayout>
      {MetricToRoute.map(({ metric, route, component: Component }) => (
        <PathRoute path={route} key={metric}>
          {(match) => (
            <div
              className={styles.Frame}
              data-active={!!match}
              aria-hidden={!match}
            >
              <Sticky delay={300}>
                {!!match && (
                  <Backdrop
                    active={!!match}
                    onOutsideClick={nav.summaryData.on({})}
                    isTransparent
                    isHalfWidth
                  >
                    <Component />
                  </Backdrop>
                )}
              </Sticky>
            </div>
          )}
        </PathRoute>
      ))}
    </ViewStack>
  );
}
